import { useMemo } from 'react';

import type { MenuGroup, MenuItem, MenuSubItem } from 'design-system';
import {
  ChartNoAxesCombined,
  Download,
  FlaskConical,
  House,
  List,
  Newspaper,
  Plus,
  Settings
} from 'lucide-react';
import { useLocation } from 'react-router';

import {
  FWDCURVE_CATALOG_PATH,
  METADATA_FIRST_NAME_FIELD,
  METADATA_LAST_NAME_FIELD,
  SPOT_CATALOG_PATH
} from '@/config/constants';
import { groupMarketsBySector, type MarketNavigationItem } from '@/entities/market';
import { DASHBOARD_NEW_SECTION_LABEL, useDashboard } from '@/features/dashboard';
import { ExportSelectionSource, useOpenExportSelection } from '@/features/data-hub';
import { useMarketsList } from '@/features/markets/api/marketsList.options';
import { FeatureFlag } from '@/services/feature-flags';
import { useFlag } from '@/services/feature-flags/react';
import { retrieveFromUserMetadata } from '@/services/storage/userMetadata';
import { useAppDispatch, useAppSelector } from '@/store/hooks';
import { openExportDrawer } from '@/store/slices/drawer.slice';

/** A leaf matches when the current URL is exactly `to`, or a route nested under it. */
const isActivePath = (pathname: string, to: string) =>
  pathname === to || pathname.startsWith(`${to}/`);

const toMenuSubItems = (items: MarketNavigationItem[], pathname: string): MenuSubItem[] =>
  items.map(({ name, path, tag, children }) => ({
    label: name ?? '',
    tag: tag ?? undefined,
    ...(path && !children ? { to: path, active: isActivePath(pathname, path) } : {}),
    ...(children ? { items: toMenuSubItems(children, pathname) } : {})
  }));

/**
 * Builds the app's main navigation schema for `AppSidebar` (from `design-system`), replacing
 * the previous hand-rolled NavBar tree. Recomputes `active` from the current route on every
 * render, and reflects the same feature-flag-driven items the old NavBar showed
 * (personalization's Dashboard section, the Data Science prototypes link).
 */
export const useAppMenuSchema = (): MenuGroup[] => {
  const location = useLocation();
  const dispatch = useAppDispatch();
  const isPersonalizationEnabled = useFlag(FeatureFlag.PersonalizationEnabled);
  const isPrototypesEnabled = useFlag(FeatureFlag.DataSciencePrototypesEnabled);
  const tripTokenStatus = useAppSelector(state => state.tripToken.status);
  const { data: marketsData } = useMarketsList(undefined, tripTokenStatus === 'fulfilled');
  const { data: dashboardsData } = useDashboard();
  const openExportSelection = useOpenExportSelection(ExportSelectionSource.NAV_BAR);

  return useMemo(() => {
    const pathname = location.pathname;

    const marketItems = toMenuSubItems(
      groupMarketsBySector(marketsData?.groupedMarkets ?? []),
      pathname
    );

    const dashboardNames = dashboardsData ? Object.keys(dashboardsData) : [];
    const dashboardItems: MenuSubItem[] = [
      ...dashboardNames.map(name => {
        const to = `/dashboard/${encodeURIComponent(name)}`;
        return { label: name, to, active: isActivePath(pathname, to) };
      }),
      {
        label: DASHBOARD_NEW_SECTION_LABEL,
        icon: Plus,
        variant: 'muted' as const,
        to: '/dashboard/new',
        active: isActivePath(pathname, '/dashboard/new')
      }
    ];

    const catalogItems: MenuSubItem[] = [
      {
        label: 'Spot',
        to: `/catalog/${SPOT_CATALOG_PATH}`,
        active: isActivePath(pathname, `/catalog/${SPOT_CATALOG_PATH}`)
      },
      {
        label: 'Forward Curves',
        to: `/catalog/${FWDCURVE_CATALOG_PATH}`,
        active: isActivePath(pathname, `/catalog/${FWDCURVE_CATALOG_PATH}`)
      }
    ];

    const exportItems: MenuSubItem[] = [
      { label: 'Export selection', onClick: openExportSelection },
      { label: 'Past exports', onClick: () => dispatch(openExportDrawer({ showHistory: true })) }
    ];

    const coreItems: MenuItem[] = [
      {
        label: 'Markets',
        icon: ChartNoAxesCombined,
        items: marketItems
      },
      {
        label: 'Catalog',
        icon: List,
        items: catalogItems
      },
      {
        label: 'Export',
        icon: Download,
        items: exportItems
      }
    ];

    if (isPrototypesEnabled) {
      coreItems.push({
        label: 'Data Science Prototypes',
        icon: FlaskConical,
        to: '/prototypes',
        active: isActivePath(pathname, '/prototypes')
      });
    }

    if (isPersonalizationEnabled) {
      coreItems.unshift({
        label: 'Dashboard',
        icon: ChartNoAxesCombined,
        items: dashboardItems
      });
    }

    const analystItems: MenuItem[] = [
      {
        label: 'Morning Intel',
        icon: Newspaper,
        to: '/morning-intel',
        active: isActivePath(pathname, '/morning-intel')
      }
    ];

    const homeItem: MenuItem = {
      label: 'Home',
      icon: House,
      to: '/',
      active: isActivePath(pathname, '/')
    };

    const menuSchema: MenuGroup[] = [
      { items: [homeItem] },
      { label: 'Core', items: coreItems },
      { label: 'GX Crude Analyst', variant: 'ai', items: analystItems },
      {
        placement: 'footer',
        items: [
          {
            label: 'Settings',
            icon: Settings,
            to: '/profile',
            active: isActivePath(pathname, '/profile')
          }
        ]
      }
    ];

    return menuSchema;
  }, [
    location.pathname,
    marketsData,
    dashboardsData,
    isPersonalizationEnabled,
    isPrototypesEnabled,
    openExportSelection,
    dispatch
  ]);
};

/** The signed-in user's display name, shown in the sidebar's footer user card. */
export const useSidebarUserName = () => {
  const { userEmail, metadata } = useAppSelector(state => state.tripToken);

  return useMemo(() => {
    const firstName = retrieveFromUserMetadata(metadata || [], METADATA_FIRST_NAME_FIELD);
    const lastName = retrieveFromUserMetadata(metadata || [], METADATA_LAST_NAME_FIELD);
    if (firstName || lastName) return (firstName ? `${firstName} ` : '') + lastName;
    return userEmail;
  }, [metadata, userEmail]);
};
